import React from 'react';
import {
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import InfoTooltip from '@/components/InfoTooltip/InfoTooltip';
import { useGetAudienceDesignatorOptions } from '@/api/hooks/data-mappings/useGetAudienceDesignatorOptions';

export default function AudienceDesignator({
  formValues,
  isUpdating,
  onAudienceDesignatorChange,
  removeAudienceDesignator,
  addAudienceDesignator,
}) {
  const { t } = useTranslation();
  const { audienceDesignatorOptions } = useGetAudienceDesignatorOptions();

  const getCategoryLabel = (category) => {
    if (category === null) {
      return t('audienceDesignator.categories.payment_columns', {
        defaultValue: 'Payment Columns',
      });
    }
    return t(`audienceDesignator.categories.${category}`, {
      defaultValue: category,
    });
  };

  const getFieldLabel = (field) =>
    t(`audienceDesignator.fields.${field}`, { defaultValue: field });

  const getPreviewLabel = (designator) => {
    const option = audienceDesignatorOptions?.find(
      (opt) => opt.value === designator,
    );

    if (!option) {
      return `[${designator}]`;
    }

    const fieldLabel = getFieldLabel(option.field);

    if (option.category) {
      return `[${getCategoryLabel(option.category)} ${fieldLabel}]`;
    }

    return `[${fieldLabel}]`;
  };

  const sortedOptions = [...audienceDesignatorOptions].sort((a, b) => {
    if (a.category === null && b.category === null) return 0;
    if (a.category === null) return -1;
    if (b.category === null) return 1;
    return a.category.localeCompare(b.category);
  });

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <div className='custom-select'>
          <FormControl component='fieldset'>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
              }}>
              <FormLabel component='legend' style={{ marginBottom: '0' }}>
                Audience Designator
              </FormLabel>
              <InfoTooltip
                title='Create a composite key for audience designation by selecting multiple columns from the available data columns'
                placement='right'
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'center',
                marginTop: '8px',
              }}>
              {formValues.audience_designators?.map((designator, index) => (
                <React.Fragment key={index}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                      minWidth: '220px',
                    }}>
                    <Autocomplete
                      options={sortedOptions}
                      groupBy={(option) => getCategoryLabel(option.category)}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? option
                          : getFieldLabel(option.field)
                      }
                      value={
                        sortedOptions.find((opt) => opt.value === designator) ||
                        null
                      }
                      onChange={(_, newValue) =>
                        onAudienceDesignatorChange(newValue?.value || '', index)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select Column'
                          variant='standard'
                          size='small'
                        />
                      )}
                      disabled={!isUpdating('data-mapping')}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value?.value
                      }
                      sx={{ minWidth: 200, marginRight: '8px' }}
                    />
                    <Delete
                      onClick={() => removeAudienceDesignator(index)}
                      style={{
                        cursor: 'pointer',
                        color: '#f44336',
                        fontSize: '18px',
                        marginTop: '20px',
                      }}
                    />
                  </div>
                  {index <
                    (formValues.audience_designators?.length || 0) - 1 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        fontSize: '18px',
                        color: '#666',
                        fontWeight: 'bold',
                        marginTop: '20px',
                      }}>
                      -
                    </div>
                  )}
                </React.Fragment>
              ))}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title='Add Column' placement='top'>
                  <span>
                    <IconButton
                      color='primary'
                      disabled={!isUpdating('data-mapping')}
                      onClick={addAudienceDesignator}
                      size='small'
                      style={{
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '6px',
                      }}>
                      <Add style={{ fontSize: '18px' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            {/* Composite Key Preview */}
            {formValues.audience_designators &&
              formValues.audience_designators.length > 0 && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}>
                  <div
                    style={{
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      color: '#555',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    Composite Key Preview:
                    <InfoTooltip
                      title="This is how the audience designator will be formatted on the audience page. Each selected column's value will be combined with hyphens to create a unique identifier."
                      placement='right'
                      size='small'
                    />
                  </div>
                  <div
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      color: '#2196f3',
                      fontWeight: '500',
                    }}>
                    {formValues.audience_designators
                      .filter(
                        (designator) => designator && designator.trim() !== '',
                      )
                      .map((designator) => getPreviewLabel(designator))
                      .join('-') || '[No columns selected]'}
                  </div>
                </div>
              )}
          </FormControl>
        </div>
      </GridItem>
    </GridContainer>
  );
}
